import type { ProductDetails } from '../../commerce/shopify.types';

export interface Badge {
  label: string;
  /** Valeur sur cinq, pour les jauges. */
  niveau?: number;
  /** Note sur cinq : rendue en étoiles plutôt qu'en jauge. */
  note?: number;
  ton: 'neutre' | 'taille' | 'durete' | 'avis';
}

const TAILLE_COURTE: Record<string, string> = {
  Petit: 'Petit chien',
  Moyen: 'Chien moyen',
  Grand: 'Grand chien',
};

/**
 * Les mêmes repères que sur les fiches du site : à qui c'est destiné, la
 * dureté, la durée de mastication et les avis. Rien n'est inventé : un champ
 * absent du catalogue ne produit tout simplement pas de badge.
 */
export function badgesFor(details: ProductDetails | undefined): Badge[] {
  if (!details) return [];
  const badges: Badge[] = [];

  if (details.tailles.length === 3) {
    badges.push({ label: 'Tous les chiens', ton: 'taille' });
  } else {
    for (const taille of details.tailles) {
      badges.push({ label: TAILLE_COURTE[taille] ?? taille, ton: 'taille' });
    }
  }

  for (const duree of details.dureeMastication) {
    badges.push({ label: `Mastication ${duree.toLowerCase()}`, ton: 'neutre' });
  }

  if (details.durete !== undefined) {
    badges.push({ label: 'Dureté', niveau: details.durete, ton: 'durete' });
  }
  if (details.duree !== undefined) {
    badges.push({ label: 'Durée', niveau: details.duree, ton: 'durete' });
  }
  if (details.digestion !== undefined) {
    badges.push({ label: 'Digestion', niveau: details.digestion, ton: 'durete' });
  }
  if (details.ageMinimumMois !== undefined) {
    badges.push({ label: `Dès ${details.ageMinimumMois} mois`, ton: 'neutre' });
  }
  if (details.poidsMinimumKg !== undefined) {
    badges.push({ label: `À partir de ${details.poidsMinimumKg} kg`, ton: 'neutre' });
  }
  if (details.pourGloutons === true) {
    badges.push({ label: 'Convient aux gloutons', ton: 'neutre' });
  }
  if (details.note !== undefined && details.nombreAvis) {
    const avis = details.nombreAvis === 1 ? '1 avis' : `${details.nombreAvis} avis`;
    badges.push({
      label: `${details.note.toFixed(1).replace('.', ',')} sur 5, ${avis}`,
      note: details.note,
      ton: 'avis',
    });
  }

  return badges;
}

/** Rend une jauge sur cinq, lisible aussi sans la couleur. */
function jauge(niveau: number): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'badge__gauge';
  for (let index = 1; index <= 5; index += 1) {
    const cran = document.createElement('span');
    cran.className = 'badge__gauge-step';
    if (index <= niveau) cran.classList.add('is-on');
    wrap.append(cran);
  }
  return wrap;
}

/**
 * Étoiles de la note, remplies au demi-point près par un dégradé.
 * Le texte de la note reste écrit à côté : l'information ne dépend pas du visuel.
 */
export function etoiles(note: number): HTMLElement {
  const wrap = document.createElement('span');
  wrap.className = 'badge__stars';
  wrap.setAttribute('aria-hidden', 'true');

  for (let index = 1; index <= 5; index += 1) {
    const etoile = document.createElement('span');
    etoile.className = 'badge__star';
    const remplissage = Math.max(0, Math.min(1, note - (index - 1)));
    etoile.style.setProperty('--fill', `${(remplissage * 100).toFixed(0)}%`);
    etoile.textContent = '★';
    wrap.append(etoile);
  }
  return wrap;
}

export function renderBadges(badges: Badge[]): HTMLElement {
  const list = document.createElement('ul');
  list.className = 'badges';

  for (const badge of badges) {
    const item = document.createElement('li');
    item.className = 'badge';
    item.dataset['ton'] = badge.ton;

    if (badge.note !== undefined) item.append(etoiles(badge.note));

    const texte = document.createElement('span');
    texte.textContent =
      badge.niveau === undefined ? badge.label : `${badge.label} ${badge.niveau}/5`;
    item.append(texte);

    if (badge.niveau !== undefined) item.append(jauge(badge.niveau));
    list.append(item);
  }

  return list;
}
